import {
  Ban, BarChart3, CalendarClock, CalendarDays, CalendarSearch, ClipboardList, Cog, Compass,
  Filter, Gauge, Headphones, HelpCircle, History, Inbox, LayoutDashboard, ListChecks,
  MessageSquare, MessageSquarePlus, MousePointerClick, PartyPopper, Phone, PhoneCall, RefreshCw, Repeat, RotateCw,
  Shield, SlidersHorizontal, Sparkles, Table2, Trophy, UserCircle, UserCog, UserX, Users,
  Voicemail,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

/** Kapitel der Tour — gruppieren die Schritte und zeigen den Fortschritt. */
export type ChapterId = 'start' | 'calls' | 'contacts' | 'plan' | 'mgmt' | 'you';

export interface Chapter {
  id: ChapterId;
  label: string;
  icon: LucideIcon;
}

export const CHAPTERS: Chapter[] = [
  { id: 'start',    label: 'Ankommen',   icon: Compass },
  { id: 'calls',    label: 'Anrufen',    icon: PhoneCall },
  { id: 'contacts', label: 'Kontakte',   icon: Users },
  { id: 'plan',     label: 'Mein Plan',  icon: CalendarDays },
  { id: 'mgmt',     label: 'Verwaltung', icon: Shield },
  { id: 'you',      label: 'Dein Platz', icon: SlidersHorizontal },
];

/** Was die angemeldete Person überhaupt sehen darf — steuert, welche Schritte kommen. */
export interface TourContext {
  canSeeTeam: boolean;
  canSeeAdmin: boolean;
  canSeeInsights: boolean;
  canSeeScripts: boolean;
}

export interface TourStep {
  id: string;
  chapter: ChapterId;
  icon: LucideIcon;
  title: string;
  body: ReactNode;
  /** Kurzer Praxis-Hinweis unter dem Text */
  tip?: ReactNode;
  /** Seite, die vor diesem Schritt geöffnet wird */
  route?: string;
  /** Element, auf das der Scheinwerfer zeigt (CSS-Selektor) */
  target?: string;
  /** Karte bewusst mittig zeigen, auch wenn ein Ziel existiert */
  center?: boolean;
  /** Konto-Menü für diesen Schritt aufziehen, um seinen Inhalt zu zeigen */
  openMenu?: boolean;
  /** Schritt nur zeigen, wenn die Rolle den Bereich sieht */
  visible?: (ctx: TourContext) => boolean;
}

/** Menüpunkt in der Seitenleiste */
const nav = (path: string) => `[data-tour="nav"] a[href="${path}"]`;

const ALL_STEPS: TourStep[] = [
  // ── Ankommen ────────────────────────────────────────────────────────────
  {
    id: 'welcome',
    chapter: 'start',
    icon: Sparkles,
    title: 'Willkommen im Callcenter',
    body: (
      <>
        Schön, dass du da bist. Hier läuft alles zusammen, was mit Telefonieren zu tun
        hat: <strong>wen du anrufen sollst</strong>, <strong>was besprochen wurde</strong> und{' '}
        <strong>wann du dich wieder melden musst</strong>.
        <br />
        <br />
        Wir gehen einmal gemeinsam durch die App — du siehst dabei die echten Seiten,
        angeklickt wird nichts. Etwa drei Minuten.
      </>
    ),
    tip: <>Du kannst jederzeit abbrechen und die Tour später erneut starten.</>,
    route: '/',
    center: true,
  },
  {
    id: 'nav',
    chapter: 'start',
    icon: Compass,
    title: 'Das Menü ist dein Wegweiser',
    body: (
      <>
        Links stehen alle Bereiche, in Gruppen sortiert: <strong>Anrufen</strong> (deine
        tägliche Arbeit), <strong>Kontakte</strong> (Leute nachschlagen),{' '}
        <strong>Nachrichten</strong>, <strong>Mein Plan</strong> (Termine &amp;
        Wochenplan) und <strong>Verwaltung</strong>. Du siehst nur die Bereiche, für die
        du freigeschaltet bist.
      </>
    ),
    tip: <>Wir schauen uns gleich jeden Bereich einzeln an.</>,
    route: '/',
    target: '[data-tour="nav"]',
  },
  {
    id: 'dash-stats',
    chapter: 'start',
    icon: LayoutDashboard,
    title: 'Dein Tag auf einen Blick',
    body: (
      <>
        Das Dashboard ist deine Startseite. Die Kacheln zeigen, wo du heute stehst:
        geführte Anrufe, wie viele davon jemanden erreicht haben, neue Interessenten,
        offene Voicemails und wie viele Leute noch auf der Anrufliste stehen.
      </>
    ),
    tip: <>Ein Klick auf eine Kachel bringt dich direkt in die passende Liste.</>,
    route: '/',
    target: '[data-tour="dash-stats"]',
  },
  {
    id: 'dash-widgets',
    chapter: 'start',
    icon: Gauge,
    title: 'Was heute liegen geblieben ist',
    body: (
      <>
        Darunter stehen die Dinge, die heute Aufmerksamkeit brauchen: neu eingegangene
        Interessenten, <strong>Probetrainings ohne Rückruf</strong> (mit Ein-Klick-Knopf,
        um am Vortag zu erinnern), fällige Wiedervorlagen und deine letzten Anrufe.
      </>
    ),
    route: '/',
    target: '[data-tour="dash-widgets"]',
  },
  {
    id: 'dash-nextcall',
    chapter: 'start',
    icon: Phone,
    title: 'Direkt loslegen',
    body: (
      <>
        Wenn du nicht lange suchen willst: Dieser Knopf schnappt sich den nächsten
        offenen Interessenten und startet den Anruf sofort. Der schnellste Weg vom
        Hinsetzen zum ersten Gespräch.
      </>
    ),
    route: '/',
    target: '[data-tour="dash-nextcall"]',
  },

  // ── Anrufen ─────────────────────────────────────────────────────────────
  {
    id: 'queue-intro',
    chapter: 'calls',
    icon: ListChecks,
    title: 'Die Anrufliste — deine Arbeitsliste',
    body: (
      <>
        Hier stehen alle Leute, die angerufen werden sollen. Ganz oben die{' '}
        <strong>neuesten Anfragen</strong>, denn frische Interessenten sind am wertvollsten.
        Oben rechts schaltest du um, wen du gerade vor dir haben willst: Interessenten,
        Probetrainings oder bestehende Mitglieder.
      </>
    ),
    route: '/queue',
    target: '[data-tour="queue-sources"]',
  },
  {
    id: 'queue-filters',
    chapter: 'calls',
    icon: Filter,
    title: 'Die Liste auf dich zuschneiden',
    body: (
      <>
        Suche nach Name oder Nummer und filtere nach <strong>Status</strong>,{' '}
        <strong>Standort</strong>, Anzahl der bisherigen Versuche,{' '}
        <strong>Alter</strong> (in den Alterskategorien unserer Kurse: 2–3, 4–5, 6–8,
        9–11, 12–15, Erwachsene) oder <strong>Kind / Erwachsen</strong>. So machst du dir
        zum Beispiel eine Liste „alle unbearbeiteten 6- bis 8-Jährigen in Wilmersdorf"
        und arbeitest die konzentriert ab.
      </>
    ),
    tip: (
      <>
        Status und Standort sind <strong>Mehrfachauswahlen</strong>: aufklappen und so
        viele ankreuzen, wie du brauchst – etwa „Nicht erreicht 1x + 2x" für zwei Standorte
        auf einmal.
      </>
    ),
    route: '/queue',
    target: '[data-tour="queue-filters"]',
  },
  {
    id: 'queue-table',
    chapter: 'calls',
    icon: Table2,
    title: 'Sortieren per Klick',
    body: (
      <>
        Jeder Spaltenkopf ist anklickbar und sortiert die Liste — auf und ab. Besonders
        nützlich sind <strong>„Wartet seit"</strong> (wer wartet am längsten auf einen
        Rückruf?) und <strong>„Versuche"</strong> (wen haben wir schon fünfmal nicht
        erreicht?).
      </>
    ),
    route: '/queue',
    target: '[data-tour="queue-table"]',
  },
  {
    id: 'queue-enqueue',
    chapter: 'calls',
    icon: MousePointerClick,
    title: 'Liste in den Power-Dialer schicken',
    body: (
      <>
        Wenn die Liste passt, übergibst du sie mit diesem Knopf komplett an den
        Power-Dialer. Ab da musst du keine Nummer mehr suchen — die App ruft der Reihe
        nach durch.
      </>
    ),
    route: '/queue',
    target: '[data-tour="queue-enqueue"]',
  },
  {
    id: 'dialer',
    chapter: 'calls',
    icon: PhoneCall,
    title: 'Der Power-Dialer telefoniert für dich',
    body: (
      <>
        Sobald eine Liste übergeben ist, erscheint rechts der Power-Dialer:{' '}
        <strong>Start</strong> wählt die erste Nummer, nach jedem Gespräch trägst du das
        Ergebnis ein und die App wählt automatisch weiter. Stumm schalten, pausieren und
        auflegen liegen direkt daneben.
        <br />
        <br />
        Während des Gesprächs öffnet sich das Anruffenster: dort siehst du das
        Kundenprofil, hältst Notizen fest, setzt den Status und legst mit zwei Klicks
        eine Wiedervorlage an.
        <br />
        <br />
        Die <strong>Kundenakte öffnet sich beim Wählen von selbst</strong> — du musst
        die Person während des Gesprächs nicht mehr suchen. Ein Klick auf den{' '}
        <strong>Namen</strong> im Anruffenster oder im Dialer führt jederzeit dorthin.
        Wer lieber in der Arbeitsliste stehen bleibt, schaltet das Öffnen unter
        „Einstellungen" ab.
      </>
    ),
    tip: (
      <>
        Zum Üben gibt es unter „Einstellungen" einen <strong>Test-Modus</strong> — dann
        gehen alle Anrufe an deine eigene Nummer statt an echte Kunden.
      </>
    ),
    center: true,
  },
  {
    id: 'anruffenster-platz',
    chapter: 'calls',
    icon: PhoneCall,
    title: 'Das Anruffenster kannst du wegschieben',
    body: (
      <>
        Das Anruffenster liegt mitten auf dem Bildschirm — praktisch beim Reden, im Weg
        beim Buchen. Zwei Handgriffe helfen: die <strong>Kopfzeile ziehen</strong>{' '}
        verschiebt das Fenster, der Knopf <strong>„klein"</strong> oben rechts schrumpft es
        zu einer Blase unten rechts. Ein Klick auf die Blase holt es zurück.
        <br />
        <br />
        In beiden Fällen läuft das Gespräch weiter — du kannst also währenddessen in Ruhe
        einen Termin suchen und buchen, statt erst nach dem Telefonat.
      </>
    ),
    center: true,
  },
  {
    id: 'status-automatik',
    chapter: 'contacts',
    icon: Repeat,
    title: 'Wann der Status von allein springt',
    body: (
      <>
        Der Telefon-Status läuft nach jedem <strong>erfolglosen</strong> Anruf eine Stufe
        weiter: Unbearbeitet → Nicht erreicht 1x … 5x → Nicht vereinbart. Und er springt
        auf <strong>„Vereinbart"</strong>, sobald ein Probetraining gebucht ist.
        <br />
        <br />
        Alles andere — FollowUp, Mail geschickt, Warteliste — setzt ihr selbst.{' '}
        <strong>„Vereinbart"</strong> und <strong>„Blacklist"</strong> überschreibt das
        System nie. Mehrere Wählversuche in fünf Minuten zählen als einer.
      </>
    ),
    tip: (
      <>
        Fahr in einer Liste über einen Status — dort steht, wie er zustande kommt. Im
        Verlauf der Akte ist jeder Wechsel mit <strong>AUTOMATISCH</strong> oder{' '}
        <strong>VON HAND</strong> gekennzeichnet.
      </>
    ),
    center: true,
  },
  {
    id: 'anfrage-stornieren',
    chapter: 'contacts',
    icon: Ban,
    title: 'Doppelte Anfragen stornieren statt löschen',
    body: (
      <>
        Hat sich jemand doppelt eingebucht, räumst du die überflüssige Anfrage über das
        Zeichen in der <strong>Status-Spalte</strong> der Interessenten-Liste weg. Ein
        Grund ist Pflicht, danach ist die Anfrage aus der Arbeitsliste raus.
        <br />
        <br />
        <strong>Gelöscht wird dabei nichts.</strong> Die Kundenakte bleibt bestehen — sie
        wird nach Ablauf der gesetzlichen Frist ohnehin automatisch entfernt. Der Schritt
        steht mit deinem Namen in der Akte und lässt sich zurücknehmen.
      </>
    ),
    route: '/leads',
    target: '[data-tour="leads-table"]',
  },
  {
    id: 'pagehelp',
    chapter: 'calls',
    icon: HelpCircle,
    title: 'Jede Seite erklärt sich selbst',
    body: (
      <>
        Oben auf jeder Seite steht ein kurzer Hinweis, wozu der Bereich da ist. Brauchst
        du ihn nicht mehr, klick ihn weg — er bleibt weg, bis du ihn über{' '}
        <strong>„Was ist das?"</strong> wieder einblendest. Diese Entscheidung gilt nur
        für dich.
      </>
    ),
    route: '/queue',
    target: '[data-tour="pagehelp"]',
  },
  {
    id: 'callbacks',
    chapter: 'calls',
    icon: CalendarClock,
    title: 'Wiedervorlagen — versprochene Rückrufe',
    body: (
      <>
        „Rufen Sie mich Donnerstag nochmal an" — genau das landet hier. Als Liste oder
        als Wochenkalender, überfällige Rückrufe sind hervorgehoben. Drei Knöpfe an
        jeder Zeile: die <strong>Uhr</strong> markiert „in Bearbeitung" (versucht,
        aber nicht direkt erreicht — bleibt sichtbar), <strong>Häkchen</strong> und
        <strong> Kreuz</strong> haken erledigt bzw. abgebrochen ab und verschwinden
        danach aus der Liste. Du siehst auch die Termine der Kolleginnen und
        Kollegen, damit bei Urlaub oder Krankheit nichts liegen bleibt. In der
        Spalte <strong>Zuständig</strong> gibst du einen Rückruf weiter — an dich,
        an eine Kollegin / einen Kollegen oder zurück an „offen für alle".
        <br />
        <br />
        Dasselbe geht in der Kundenakte im Block <strong>Wiedervorlagen</strong>:
        dort legst du eine neue an (samt „Zuständig") und gibst eine offene weiter,
        ohne den Umweg über diese Liste — praktisch, wenn eine Info per Mail kommt
        und nicht im Telefonat.
      </>
    ),
    tip: <>Die App legt manche Rückrufe selbst an — etwa einen Tag vor einem Probetraining.</>,
    route: '/callbacks',
    target: nav('/callbacks'),
  },
  {
    id: 'history',
    chapter: 'calls',
    icon: History,
    title: 'Verlauf — was wurde besprochen?',
    body: (
      <>
        Jeder geführte Anruf mit Kunde, Mitarbeiter, Ergebnis und Notiz. Zum Nachlesen
        vor dem nächsten Gespräch, fürs gemeinsame Besprechen im Team und als
        CSV-Export für Auswertungen.
      </>
    ),
    route: '/history',
    target: nav('/history'),
  },
  {
    id: 'voicemails',
    chapter: 'calls',
    icon: Voicemail,
    title: 'Mailbox — wer hat draufgesprochen?',
    body: (
      <>
        Nachrichten von Anrufern, die niemanden erreicht haben. Direkt im Browser
        abhören, vor- und zurückspulen und mit einem Klick zurückrufen.{' '}
        <strong>Offene Nachrichten stehen oben</strong> — was auf „Erledigt" steht,
        wandert unter die Trennlinie.
      </>
    ),
    route: '/voicemails',
    target: nav('/voicemails'),
  },
  {
    id: 'backontrack',
    chapter: 'calls',
    icon: RotateCw,
    title: 'Reaktivierung — bevor jemand abspringt',
    body: (
      <>
        Mitglieder, von denen wir seit über 60 Tagen nichts gehört haben, sortiert nach
        „am längsten still". Ein kurzer Anruf hier ist oft der Unterschied zwischen
        Bleiben und Kündigen.
      </>
    ),
    route: '/back-on-track',
    target: nav('/back-on-track'),
  },

  // ── Kontakte ────────────────────────────────────────────────────────────
  {
    id: 'leads',
    chapter: 'contacts',
    icon: Inbox,
    title: 'Interessenten — alles aus dem Formular',
    body: (
      <>
        Jede Anfrage über die Website: wer, wann, welcher Standort, welcher Kurs und wie
        weit die Anfrage gekommen ist. Ein Klick auf den <strong>Namen</strong> öffnet das
        Kundenprofil.
        <br />
        <br />
        Wer <strong>mehrfach angefragt</strong> hat, steht in einer Zeile. Darunter siehst
        du, seit wann der Vorgang läuft und was zuletzt notiert wurde; neben dem Namen
        steht farbig, ob die Person schon einmal im <strong>Probetraining</strong> war —
        mit Datum. Fragt jemand nach mindestens 30 Tagen Pause wieder an, geht der
        Telefon-Status automatisch zurück auf „Unbearbeitet": die Anfrage zählt als neu.
      </>
    ),
    route: '/leads',
    target: nav('/leads'),
  },
  {
    id: 'leads-status',
    chapter: 'contacts',
    icon: Headphones,
    title: 'Die Status-Spalte sagt dir alles Wichtige',
    body: (
      <>
        Hat jemand über das Formular einen <strong>Rückruf angefordert</strong> (Weg
        „Callcenter" oder „KI-Call"), steht in der Status-Spalte direkt der{' '}
        <strong>Telefon-Status</strong> aus der Kundenakte — Unbearbeitet, FollowUp,
        „Nicht erreicht 2x", Vereinbart — samt Anzahl der Versuche und Datum des letzten
        Anrufs. Du siehst also ohne einen einzigen Klick, ob schon jemand dran war.
        <br />
        <br />
        Bei allen anderen steht dort, wo die Anfrage stehen geblieben ist. Wichtig sind
        die zwei Abbruch-Arten: <strong>„Formular abgebrochen"</strong> (Kontaktdaten
        eingegeben, dann verlassen — nichts gewählt) und{' '}
        <strong>„Buchung abgebrochen"</strong> (wollte selbst buchen, hat es nicht zu Ende
        gebracht). Zwei ganz verschiedene Gespräche.
      </>
    ),
    tip: <>Mit der Maus über einen Status fahren — dann steht dort, woher er kommt.</>,
    route: '/leads',
    target: '[data-tour="leads-table"]',
  },
  {
    id: 'leads-filters',
    chapter: 'contacts',
    icon: Filter,
    title: 'Nach Alter, Kind/Erwachsen und Weg filtern',
    body: (
      <>
        Der Alters-Filter kennt die <strong>Alterskategorien unserer Kurse</strong>:
        2–3, 4–5, 6–8, 9–11, 12–15 und Erwachsene — praktisch bei einem Aufnahmestopp.
        Daneben <strong>Kind / Erwachsen</strong>, das auch dann greift, wenn kein Alter
        erfasst ist (dann zählt, was im Formular angegeben wurde). Und über{' '}
        <strong>Weg</strong> holst du dir gezielt die Rückruf-Anfragen oder die, die gar
        keinen Weg gewählt haben. Standort, Weg, Status und Telefon-Status sind{' '}
        <strong>Mehrfachauswahlen</strong> — aufklappen und mehrere Werte gleichzeitig
        ankreuzen.
      </>
    ),
    tip: <>Ein gesetzter Filter ist selbst eingefärbt — du siehst sofort, was gerade aktiv ist.</>,
    route: '/leads',
    target: '[data-tour="leads-filters"]',
  },
  {
    id: 'leads-call',
    chapter: 'contacts',
    icon: PhoneCall,
    title: 'Direkt von hier aus anrufen',
    body: (
      <>
        Zeilen links ankreuzen und oben auf <strong>„anrufen"</strong> — der Power-Dialer
        arbeitet die Auswahl ab, genau wie in der Kundenliste. Über{' '}
        <strong>„Kampagne"</strong> gehen Mail oder SMS an dieselbe Auswahl. Für einen
        einzelnen Kontakt reicht das kleine <strong>+</strong> neben der Nummer.
      </>
    ),
    tip: <>Gesperrte Kontakte und Anfragen ohne Nummer lassen sich gar nicht ankreuzen — beim Darüberfahren steht, warum.</>,
    route: '/leads',
    target: '[data-tour="leads-actions"]',
  },
  {
    id: 'abandoned',
    chapter: 'contacts',
    icon: UserX,
    title: 'Abgebrochene Buchungen — die warmen Kontakte',
    body: (
      <>
        Leute, die im Formular ihre Kontaktdaten eingegeben, dann aber{' '}
        <strong>weder einen Kurs gewählt noch einen Rückruf angefordert</strong> haben.
        Die sind schon halb überzeugt — je frischer der Abbruch, desto besser stehen die
        Chancen am Telefon.
      </>
    ),
    route: '/abandoned-bookings',
    target: nav('/abandoned-bookings'),
  },
  {
    id: 'customers',
    chapter: 'contacts',
    icon: UserCircle,
    title: 'Kunden — die komplette Kartei',
    body: (
      <>
        Zum gezielten Nachschlagen: hier findest du jede Person, die wir kennen, und
        legst neue Kontakte oder Geschwister an. Die tägliche Akquise läuft über die
        Anrufliste — hier suchst du einzelne Leute.
      </>
    ),
    route: '/customers',
    target: nav('/customers'),
  },
  {
    id: 'customer-detail',
    chapter: 'contacts',
    icon: ClipboardList,
    title: 'Das Kundenprofil ist die ganze Geschichte',
    body: (
      <>
        Klickst du eine Person an, öffnet sich ihre Akte: Kontaktdaten, woher sie kam,
        gebuchte Probetrainings mit Status, jeder Anruf, jede verschickte Nachricht und
        alle Notizen — lückenlos von oben nach unten.
      </>
    ),
    tip: <>Vor jedem Anruf kurz reinschauen spart peinliche Momente im Gespräch.</>,
    center: true,
  },
  {
    id: 'campaign',
    chapter: 'contacts',
    icon: MessageSquare,
    title: 'Nachrichten — SMS, WhatsApp und E-Mail',
    body: (
      <>
        Wer nicht ans Telefon geht, bekommt eine Nachricht. Mit Vorlage, als Freitext
        oder als <strong>KI-Entwurf</strong>, den du vor dem Senden noch anpasst. Jeder
        Versand landet automatisch im Verlauf der Kundenakte.
      </>
    ),
    route: '/campaign',
    target: nav('/campaign'),
  },

  // ── Mein Plan ───────────────────────────────────────────────────────────
  {
    id: 'availability',
    chapter: 'plan',
    icon: CalendarSearch,
    title: 'Freie Probetermine finden',
    body: (
      <>
        Die wichtigste Frage im Gespräch: „Wann könnte ich denn kommen?" Hier siehst du
        sofort, welche Kurse noch Platz haben — und buchst den Termin direkt für die
        Person, mit der du gerade sprichst.
      </>
    ),
    route: '/availability',
    target: nav('/availability'),
  },
  {
    id: 'schedule',
    chapter: 'plan',
    icon: CalendarDays,
    title: 'Wochenplan — was läuft wann?',
    body: (
      <>
        Der komplette Stundenplan je Standort. Praktisch, wenn jemand fragt, ob es
        Kindertraining auch am Samstag gibt, oder wann die nächste Anfängergruppe
        startet.
      </>
    ),
    route: '/schedule',
    target: nav('/schedule'),
  },

  // ── Verwaltung (rollenabhängig) ─────────────────────────────────────────
  {
    id: 'scripts',
    chapter: 'mgmt',
    icon: ClipboardList,
    title: 'Skripte — der rote Faden fürs Gespräch',
    body: (
      <>
        Gesprächsleitfäden und Vorlagen, die im Anruffenster auftauchen. Hier werden sie
        gepflegt: ein gutes Skript sorgt dafür, dass alle im Team dieselben Fragen
        stellen und nichts vergessen.
      </>
    ),
    route: '/skripte',
    target: nav('/skripte'),
    visible: (c) => c.canSeeScripts,
  },
  {
    id: 'insights',
    chapter: 'mgmt',
    icon: BarChart3,
    title: 'Auswertungen — die Zahlen dahinter',
    body: (
      <>
        Erreichungsquote, Erscheinungsquote und Akquise-Kennzahlen je Zeitraum und
        Standort. Zeigt schwarz auf weiß, ob mehr Anrufe auch mehr Probetrainings
        bringen.
      </>
    ),
    route: '/insights',
    target: nav('/insights'),
    visible: (c) => c.canSeeInsights,
  },
  {
    id: 'team',
    chapter: 'mgmt',
    icon: Trophy,
    title: 'Team — alle Anrufe und das Leaderboard',
    body: (
      <>
        Der Blick über das eigene Postfach hinaus: alle Anrufe des Teams und eine
        Rangliste, wer wie viel telefoniert und wie erfolgreich. Gut fürs gemeinsame
        Besprechen — und ehrlich gesagt auch ganz motivierend.
      </>
    ),
    route: '/team',
    target: nav('/team'),
    visible: (c) => c.canSeeTeam,
  },
  {
    id: 'admin',
    chapter: 'mgmt',
    icon: Shield,
    title: 'Admin — die Schaltzentrale',
    body: (
      <>
        Benutzer freischalten, Telefonnummern verwalten, Sperrlisten pflegen,
        Nachrichten-Vorlagen und Gesprächsprotokolle bearbeiten sowie der globale
        Test-Modus fürs ganze Team.
      </>
    ),
    route: '/admin',
    target: nav('/admin'),
    visible: (c) => c.canSeeAdmin,
  },

  // ── Dein Arbeitsplatz ───────────────────────────────────────────────────
  {
    id: 'usermenu',
    chapter: 'you',
    icon: UserCog,
    title: 'Dein Konto — oben in der Leiste',
    body: (
      <>
        Ganz oben unter dem Logo stehen dein Name und deine Rolle. Ein Klick darauf
        öffnet alles, was nur dich betrifft — schauen wir gleich rein.
      </>
    ),
    target: '[data-tour="usermenu"]',
  },
  {
    id: 'usermenu-open',
    chapter: 'you',
    icon: SlidersHorizontal,
    title: 'Was im Konto-Menü steckt',
    body: (
      <>
        <strong>Einstellungen</strong> für dein Dialer-Tempo,{' '}
        <strong>hell oder dunkel</strong> für die Augen, <strong>Abmelden</strong> — und{' '}
        <strong>„Einführung neu starten"</strong>, falls du diese Tour noch einmal sehen
        willst. Alles bleibt für dich gespeichert.
      </>
    ),
    tip: <>Genau hier holst du dir die Tour zurück, wenn du dir bei etwas unsicher bist.</>,
    target: '[data-tour="usermenu-panel"]',
    openMenu: true,
  },
  {
    id: 'feedback',
    chapter: 'you',
    icon: MessageSquarePlus,
    title: 'Fehlt etwas? Sag es uns',
    body: (
      <>
        Unter deinem Konto steht <strong>„Feedback"</strong>. Von jeder Seite aus meldest du
        damit, was fehlt oder hakt — ein, zwei Sätze reichen. Ein Foto der Seite, der Link und
        die technischen Angaben gehen automatisch als Ticket mit.
      </>
    ),
    tip: <>Das Foto lässt sich abwählen; weitere Bilder hängst du per Büroklammer, Ziehen oder Strg+V an.</>,
    target: '[data-tour="melden"]',
  },
  {
    id: 'settings',
    chapter: 'you',
    icon: Cog,
    title: 'Der Dialer nach deinem Tempo',
    body: (
      <>
        Wie lange die Pause zwischen zwei Anrufen ist, wie oft eine Nummer erneut
        probiert wird und ob es nach dem Gespräch automatisch weitergeht. Hier liegt
        auch dein persönlicher <strong>Test-Modus</strong>: einmal angeschaltet, gehen
        alle Anrufe an deine eigene Nummer — zum gefahrlosen Üben.
      </>
    ),
    route: '/settings',
    target: '[data-tour="settings-page"]',
  },
  {
    id: 'reload-hint',
    chapter: 'you',
    icon: RefreshCw,
    title: 'Wenn mal nichts lädt',
    body: (
      <>
        Die Listen holen ihre Daten aus dem CRM. Braucht das ungewöhnlich lange, erscheint
        oben auf der Seite von selbst ein Hinweis mit einem{' '}
        <strong>„Neu laden"</strong>-Knopf — und die Liste selbst sagt dir, ob sie noch
        lädt, ob die Verbindung nicht antwortet oder ob wirklich nichts da ist.
        <br />
        <br />
        Der zuverlässigste Griff bleibt der Browser-Refresh:{' '}
        <strong>{'⌘'} + R</strong> auf dem Mac, <strong>F5</strong> auf Windows. Das
        hilft besonders direkt nach einem Update, wenn die geöffnete Seite noch auf die
        alte Fassung zeigt.
      </>
    ),
    tip: <>Passiert das öfter an derselben Stelle: Uhrzeit und Seite melden — dann können wir gezielt nachmessen.</>,
    center: true,
  },
  {
    id: 'done',
    chapter: 'you',
    icon: PartyPopper,
    title: 'Fertig — viel Erfolg am Telefon!',
    body: (
      <>
        Das war die Runde durchs Haus. Der schnellste Start in den Tag:{' '}
        <strong>Dashboard öffnen</strong> → <strong>„Nächsten Anruf starten"</strong> —
        alles Weitere führt dich die App entlang.
        <br />
        <br />
        Wenn etwas fehlt oder hakt: sag Bescheid, das landet auf der Liste.
      </>
    ),
    center: true,
  },
];

/** Schritte für diese Rolle — Bereiche ohne Zugriff fallen ganz raus. */
export function buildSteps(ctx: TourContext): TourStep[] {
  return ALL_STEPS.filter((s) => !s.visible || s.visible(ctx));
}
